import React, { useEffect, useState } from "react";
import client, { videoUrl, formatErr } from "../../lib/api";
import { toast } from "sonner";
import { Check, X, RotateCw, Flag, Cpu } from "lucide-react";

const statuses = [
    { id: "pending", label: "Pending" },
    { id: "approved", label: "Approved" },
    { id: "rejected", label: "Rejected" },
    { id: "resubmit", label: "Resubmit" },
];

export default function AdminReview() {
    const [filter, setFilter] = useState("pending");
    const [list, setList] = useState([]);
    const [active, setActive] = useState(null);
    const [feedback, setFeedback] = useState("");
    const [loading, setLoading] = useState(false);

    const load = () => {
        client.get("/admin/submissions", { params: { status: filter, limit: 200 } }).then((r) => {
            const items = r.data.items;
            setList(items);
            if (items.length > 0 && !active) setActive(items[0]);
            else if (items.length === 0) setActive(null);
        });
    };

    useEffect(() => {
        setActive(null);
        load();
        // eslint-disable-next-line
    }, [filter]);

    const review = async (status) => {
        if (!active) return;
        setLoading(true);
        try {
            await client.post(`/admin/submissions/${active.id}/review`, { status, feedback });
            toast.success(`Marked ${status}`);
            setFeedback("");
            setActive(null);
            load();
        } catch (e) {
            toast.error(formatErr(e));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <span className="label-cap text-xs text-neon-orange">Review Queue</span>
            <h1 className="font-display font-black text-4xl uppercase tracking-tight mt-1">Validate Reps</h1>

            <div className="mt-6 flex gap-2 overflow-x-auto">
                {statuses.map((s) => (
                    <button
                        key={s.id}
                        onClick={() => setFilter(s.id)}
                        data-testid={`review-filter-${s.id}`}
                        className={`shrink-0 px-4 py-2 label-cap text-xs transition-all ${
                            filter === s.id ? "bg-neon-green text-black" : "surface-1 text-zinc-400"
                        }`}
                    >
                        {s.label}
                    </button>
                ))}
            </div>

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-1">
                    <div className="space-y-2 max-h-[500px] overflow-y-auto">
                        {list.length === 0 ? (
                            <div className="surface-1 p-6 text-center text-zinc-400">No submissions.</div>
                        ) : (
                            list.map((s) => (
                                <button
                                    key={s.id}
                                    onClick={() => setActive(s)}
                                    data-testid={`review-item-${s.id}`}
                                    className={`w-full text-left surface-1 p-3 transition-all ${
                                        active?.id === s.id ? "border-neon-green" : ""
                                    }`}
                                >
                                    <div className="font-display font-bold uppercase text-sm leading-none truncate">
                                        {s.user_name}
                                    </div>
                                    <div className="text-xs text-zinc-400 mt-1 truncate">{s.challenge_title}</div>
                                    <div className="text-[10px] text-zinc-500 mt-1">
                                        {new Date(s.submitted_at).toLocaleString()}
                                    </div>
                                </button>
                            ))
                        )}
                    </div>
                </div>

                <div className="lg:col-span-2">
                    {active ? (
                        <div className="surface-2 p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="label-cap text-xs text-neon-green">Reviewing</div>
                                    <div className="font-display font-black text-2xl uppercase">
                                        {active.user_name}
                                    </div>
                                    <div className="text-zinc-400 text-sm mt-1">{active.challenge_title}</div>
                                </div>
                                <span
                                    className="text-[10px] uppercase tracking-widest px-2 py-1 surface-3"
                                    style={{
                                        color: active.status === "approved" ? "#39FF14" : active.status === "rejected" ? "#FF2424" : "#F59E0B",
                                    }}
                                >
                                    {active.status}
                                </span>
                            </div>
                            <div className="mt-4 surface-1 overflow-hidden">
                                <video
                                    key={active.id}
                                    src={videoUrl(active.id)}
                                    controls
                                    playsInline
                                    className="w-full bg-black"
                                    style={{ maxHeight: 360 }}
                                    data-testid="admin-review-video"
                                />
                            </div>
                            {active.notes && (
                                <div className="mt-3 surface-1 p-3">
                                    <div className="label-cap text-xs text-zinc-500">User Notes</div>
                                    <p className="text-sm text-zinc-300 mt-1">{active.notes}</p>
                                </div>
                            )}
                            {active.ml_exercise && (
                                <MlSummary sub={active} />
                            )}
                            <div className="mt-4">
                                <label className="label-cap text-xs text-zinc-500">Feedback</label>
                                <textarea
                                    value={feedback}
                                    onChange={(e) => setFeedback(e.target.value)}
                                    rows={3}
                                    placeholder="Form notes, technique cues, or reason for rejection..."
                                    data-testid="review-feedback"
                                    className="w-full mt-1 bg-charcoal-700 border border-charcoal-500 px-3 py-2 text-white text-sm focus:border-neon-orange focus:outline-none"
                                />
                            </div>
                            <div className="mt-4 grid grid-cols-3 gap-2">
                                <button
                                    onClick={() => review("approved")}
                                    disabled={loading}
                                    data-testid="review-approve"
                                    className="btn-neon py-3 flex items-center justify-center gap-2 disabled:opacity-50"
                                >
                                    <Check size={16} /> Approve
                                </button>
                                <button
                                    onClick={() => review("resubmit")}
                                    disabled={loading}
                                    data-testid="review-resubmit"
                                    className="surface-2 py-3 text-neon-orange label-cap text-xs flex items-center justify-center gap-2 hover:border-neon-orange disabled:opacity-50"
                                >
                                    <RotateCw size={14} /> Resubmit
                                </button>
                                <button
                                    onClick={() => review("rejected")}
                                    disabled={loading}
                                    data-testid="review-reject"
                                    className="btn-primary py-3 flex items-center justify-center gap-2 disabled:opacity-50"
                                >
                                    <X size={16} /> Reject
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="surface-1 p-12 text-center text-zinc-400">
                            <Flag className="mx-auto text-zinc-600 mb-3" size={32} />
                            Select a submission to review.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function MlSummary({ sub }) {
    const browser = sub.ml_count;
    const server = sub.server_ml_count;
    const status = sub.server_ml_status || "skipped";
    const verified = sub.server_ml_verified;
    return (
        <div
            className="mt-3 surface-1 p-3 border-l-2"
            data-testid="admin-ml-summary"
            style={{ borderLeftColor: verified ? "#39FF14" : status === "running" ? "#F59E0B" : "#52525b" }}
        >
            <div className="flex items-center gap-2">
                <Cpu size={14} className="text-neon-green" />
                <div className="label-cap text-xs text-zinc-500">ML Validation</div>
                {verified === true && (
                    <span className="ml-auto label-cap text-[9px] bg-neon-green text-black px-2 py-0.5">Verified</span>
                )}
                {verified === false && (
                    <span className="ml-auto label-cap text-[9px] bg-neon-orange text-black px-2 py-0.5">Below threshold</span>
                )}
                {status === "running" && (
                    <span className="ml-auto label-cap text-[9px] text-neon-orange animate-pulse">Processing...</span>
                )}
            </div>
            <div className="mt-2 grid grid-cols-2 gap-3 text-xs">
                <div>
                    <div className="text-zinc-500 label-cap text-[10px]">Browser ({sub.ml_exercise})</div>
                    <div className="font-display font-bold text-base mt-0.5">
                        {browser ?? "—"} reps
                    </div>
                    {sub.ml_form_score != null && (
                        <div className="text-zinc-500 text-[10px]">Form {Math.round(sub.ml_form_score * 100)}%</div>
                    )}
                </div>
                <div>
                    <div className="text-zinc-500 label-cap text-[10px]">Server</div>
                    <div className="font-display font-bold text-base mt-0.5">
                        {status === "done" ? `${server} reps` : status === "running" ? "..." : status === "error" ? "Error" : "—"}
                    </div>
                    {sub.server_ml_form_score != null && (
                        <div className="text-zinc-500 text-[10px]">Form {Math.round(sub.server_ml_form_score * 100)}%</div>
                    )}
                </div>
            </div>
            {sub.server_ml_error && (
                <div className="mt-2 text-[10px] text-neon-orange break-all">
                    {sub.server_ml_error}
                </div>
            )}
        </div>
    );
}
